import { CSSProperties, FormEvent } from 'react'
import { DEFAULT_MAX_GUESTS } from '../config'
import { PageElement } from '../schema/elements'
import { RichTextDoc } from '../schema/richText'
import { RenderContext, pick, pickValue } from './context'
import { formatMinorUnits } from './menu'
import RichText from './RichText'
import RoomPlanDrawing, { roomPlanFrameStyle } from './RoomPlan'
import { quarterHours } from './dates'

/**
 * One renderer per element type, in one file on purpose: each is between five
 * and twenty lines, they all take the same two props, and a folder of thirteen
 * single-component files would be harder to read than this is.
 *
 * Nothing here may import MUI, react-query or touch `window`. These components
 * run in Node during the prerender (PLAN.md §2.1), and the first import that
 * needs a browser breaks the published site rather than the editor, which is
 * the worst place to find out.
 */

interface Props {
  element: PageElement
  ctx: RenderContext
}

/*
  Each sub-renderer takes the element type it actually draws, so the switch at
  the bottom does the narrowing once and none of them repeats a guard that can
  never fire.
*/
type Of<T extends PageElement['type']> = Extract<PageElement, { type: T }>

const RATIO_CLASS: Record<string, string> = {
  '1:1': 'mp-image--r-1-1',
  '4:3': 'mp-image--r-4-3',
  '16:9': 'mp-image--r-16-9',
  '3:4': 'mp-image--r-3-4'
}

function alignClass(align: string) {
  return `mp-align-${align}`
}

function ImageElement({
  element,
  ctx
}: {
  element: Of<'image'>
  ctx: RenderContext
}) {
  const alt = pick(element.alt, ctx)
  const caption = pick(element.caption, ctx)
  const isPriority = ctx.priorityImageId === element.id

  /*
    The focal point is the one thing here that has to be an inline style: it is
    computed geometry, which is exactly the case CLAUDE.md leaves open. A class
    per position is not possible, the value is continuous.
  */
  const style: CSSProperties = {
    objectPosition: `${element.focalPoint.x * 100}% ${element.focalPoint.y * 100}%`
  }

  /*
    Lowercase, and spread rather than passed as a prop: React only understands
    `fetchPriority` in camelCase from v19, and this repo is on 18, where it is
    dropped with a warning instead of reaching the DOM.
  */
  const priorityAttrs: Record<string, string> = isPriority
    ? { fetchpriority: 'high' }
    : {}

  const img = (
    <img
      className={`mp-image__img ${element.fit === 'contain' ? 'mp-image__img--contain' : ''}`}
      src={ctx.resolveAssetUrl(element.assetId)}
      alt={alt}
      style={style}
      /*
        The first image on the page is almost always the LCP element. Lazy
        loading it means the browser finds it only after layout, which is the
        single most common way a content page loses half a second it cannot get
        back. Everything below it stays lazy.
      */
      loading={isPriority ? 'eager' : 'lazy'}
      decoding={isPriority ? 'sync' : 'async'}
      {...priorityAttrs}
    />
  )

  return (
    <figure
      className={`mp-image ${RATIO_CLASS[element.ratio] ?? ''} ${
        element.rounded ? 'mp-image--rounded' : ''
      }`}
    >
      {element.href ? (
        <a href={element.href} className="mp-image__link">
          {img}
        </a>
      ) : (
        img
      )}
      {caption && <figcaption className="mp-image__caption">{caption}</figcaption>}
    </figure>
  )
}

function GalleryElement({
  element,
  ctx
}: {
  element: Of<'gallery'>
  ctx: RenderContext
}) {
  return (
    <ul className={`mp-gallery mp-gallery--cols-${element.columns}`}>
      {element.assetIds.map(assetId => (
        <li key={assetId} className="mp-gallery__item">
          <img
            className="mp-gallery__img"
            src={ctx.resolveAssetUrl(assetId)}
            /*
              Decorative by construction: a gallery's meaning is the set, not
              each frame, and forcing alt text on twenty-four photos produces
              twenty-four copies of the same sentence, which is worse for a
              screen reader than silence.
            */
            alt=""
            loading="lazy"
            decoding="async"
          />
        </li>
      ))}
    </ul>
  )
}

function OpeningHoursElement({
  element,
  ctx
}: {
  element: Of<'openingHours'>
  ctx: RenderContext
}) {
  const weekdays = new Intl.DateTimeFormat(ctx.language, { weekday: 'long' })
  // 2024-01-01 was a Monday, which is weekday 0 in this app's counting.
  const nameOf = (weekday: number) =>
    weekdays.format(new Date(Date.UTC(2024, 0, 1 + weekday)))

  return (
    <table className="mp-hours">
      <tbody>
        {element.entries.map(entry => (
          <tr key={entry.weekday} className="mp-hours__row">
            <th scope="row" className="mp-hours__day">
              {nameOf(entry.weekday)}
            </th>
            <td className="mp-hours__time">
              {entry.closed ? '—' : `${entry.opensAt} – ${entry.closesAt}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function VideoElement({ element }: { element: Of<'video'> }) {
  /*
    `youtube-nocookie` and `dnt=1` because this frame sits on a page that has to
    carry a GDPR notice: the default domains set tracking cookies before anyone
    presses play. Lazy, because a video iframe pulls hundreds of kilobytes and
    would otherwise be doing it while the page is still painting.
  */
  const src =
    element.provider === 'youtube'
      ? element.url
          .replace('youtu.be/', 'www.youtube-nocookie.com/embed/')
          .replace('www.youtube.com/watch?v=', 'www.youtube-nocookie.com/embed/')
          .replace('youtube.com/watch?v=', 'www.youtube-nocookie.com/embed/')
      : `${element.url.replace('vimeo.com/', 'player.vimeo.com/video/')}?dnt=1`

  return (
    <div className="mp-video">
      <iframe
        className="mp-video__frame"
        src={src}
        title="video"
        loading="lazy"
        allowFullScreen
      />
    </div>
  )
}

/**
 * The venue on Google's free Maps Embed, the only thing the page loads from
 * Google. Without a place it is a placeholder in the editor and nothing on a
 * published page, whose row `BlockRenderer` then leaves out as well.
 */
function MapElement({ element, ctx }: { element: Of<'map'>; ctx: RenderContext }) {
  // A draft is kept as it was saved, not re-parsed, so a map from before
  // heights existed arrives without one.
  const heightClass = `mp-map--h-${element.height ?? 'M'}`

  if (!element.embedUrl) {
    const placeholder = ctx.placeholders?.map
    if (!placeholder) return null
    return (
      <div className={`mp-map mp-map--placeholder ${heightClass}`}>
        <p className="mp-map__note">{placeholder.text}</p>
        <button
          type="button"
          className="mp-button mp-button--outline mp-map__action"
          onClick={placeholder.onAction}
        >
          {placeholder.action}
        </button>
      </div>
    )
  }

  return (
    <div className={`mp-map ${heightClass}`}>
      <iframe
        className="mp-map__frame"
        // The provider's name reads the same in every language the page has.
        title="Google Maps"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
        src={element.embedUrl}
      />
    </div>
  )
}

/**
 * The one block whose contents arrive later: the contact form is phase 3. The
 * reservation and the menu draw themselves now, so they are no longer here.
 *
 * It renders a mount point rather than nothing, and that is deliberate. The
 * prerendered HTML already reserves the space and carries the id, so adding
 * the widget later is a script that fills an existing element instead of a
 * change to every published page.
 */
function MountPoint({ element }: { element: Of<'contactForm'> }) {
  return (
    <div
      className={`mp-mount mp-mount--${element.type}`}
      id={element.id}
      data-mp-block={element.type}
      data-form-id={element.formId}
    />
  )
}

/** The menu's words, in the languages this product ships; see RESERVE_COPY. */
const MENU_COPY: Record<string, Record<string, string>> = {
  pl: {
    title: 'Menu',
    lead: 'Zobacz, co dziś gotujemy.',
    open: 'Zobacz menu',
    close: 'Zamknij',
    other: 'Pozostałe'
  },
  en: {
    title: 'Menu',
    lead: 'See what we cook.',
    open: 'See the menu',
    close: 'Close',
    other: 'Other'
  }
}

/**
 * The venue's menu, behind a button.
 *
 * The dishes come with the render context and never from the document: they
 * are the menu the venue keeps in the RMS, resolved when the page is drawn,
 * so a page published in March shows May's prices. Nothing is drawn without
 * them, a button onto an empty menu being a trap.
 *
 * The button opens a sheet, the same one the booking form uses: a modal on
 * a wide screen, a drawer on a phone, with no script behind it. The opener
 * here is a checkbox, so the backdrop and the close button simply untick it.
 * Inside, the list is the guest app's own: name and a line of description,
 * the price under them, the photo at the right (`menu_mobile_FE`).
 */
function MenuElement({
  element,
  ctx
}: {
  element: Of<'menuFromPos'>
  ctx: RenderContext
}) {
  const copy = MENU_COPY[ctx.language] ?? MENU_COPY.en
  const { menu } = ctx
  if (!menu) return null

  const categories = (
    element.categoryIds.length > 0
      ? menu.categories.filter(category =>
          element.categoryIds.includes(category.id)
        )
      : menu.categories
  ).filter(category => category.dishes.length > 0)
  if (categories.length === 0) return null

  const toggleId = `${element.id}-open`
  const titleId = `${element.id}-title`

  return (
    <div
      className={`mp-menu mp-sheet-host${ctx.mode === 'edit' ? ' mp-sheet-host--inline' : ''}`}
    >
      <h2 className="mp-heading mp-heading--2 mp-align-center">{copy.title}</h2>
      <p className="mp-menu__lead">{copy.lead}</p>
      <input
        className="mp-menu__toggle mp-sheet__opener"
        type="checkbox"
        id={toggleId}
      />
      <div className="mp-button-wrap">
        <label className="mp-button mp-button--primary" htmlFor={toggleId}>
          {copy.open}
        </label>
      </div>
      <div
        className="mp-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* Hidden from assistive tech: the close button is the same control. */}
        <label
          className="mp-sheet__backdrop"
          htmlFor={toggleId}
          aria-hidden="true"
        >
          <span className="mp-visually-hidden">{copy.close}</span>
        </label>
        <div className="mp-sheet__panel mp-menu__panel">
          <div className="mp-sheet__head">
            <p className="mp-sheet__title" id={titleId}>
              {copy.title}
            </p>
            <label className="mp-sheet__close" htmlFor={toggleId}>
              <span aria-hidden="true">×</span>
              <span className="mp-visually-hidden">{copy.close}</span>
            </label>
          </div>
          {categories.map(category => {
            /*
              A group with no name is the dishes filed under no category. It
              is named here, in the page's language, and only when there are
              other groups to tell it from: over a menu that is nothing but
              that group, a heading would be a heading over everything.
            */
            const name =
              pick(category.name, ctx) ||
              (categories.length > 1 ? copy.other : '')
            return (
            <section className="mp-menu__category" key={category.id}>
              {name && <h3 className="mp-menu__category-name">{name}</h3>}
              <ul
                className={`mp-menu__dishes mp-menu__dishes--${element.layout}`}
              >
                {category.dishes.map(dish => {
                  const description = pick(dish.description, ctx)
                  return (
                    <li className="mp-menu__dish" key={dish.id}>
                      <div className="mp-menu__dish-text">
                        <div>
                          <p className="mp-menu__dish-name">
                            {pick(dish.name, ctx)}
                          </p>
                          {description && (
                            <p className="mp-menu__dish-desc">{description}</p>
                          )}
                        </div>
                        {element.showPrices && (
                          <p className="mp-menu__price">
                            {formatMinorUnits(
                              dish.price,
                              menu.currency,
                              ctx.language
                            )}
                          </p>
                        )}
                      </div>
                      {element.showPhotos && dish.imageUrl && (
                        <img
                          className="mp-menu__photo"
                          src={dish.imageUrl}
                          alt=""
                          loading="lazy"
                        />
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * The venue's rooms, and a table in one of them booked from the page.
 *
 * The plan is the picker (PLAN.md §5, mode B): every table is a radio laid over
 * the drawing, and the form under it takes the date, the time, the party and
 * who is coming. Plain HTML and no script, because the published page ships
 * none (§2.1); a form that posts is the one thing a page without a script can
 * still do, and the phase 4 widget can take this same form over and answer
 * without a reload.
 *
 * Nothing on the server answers the post yet: `menu_core_be` has no
 * `reservations` domain (config.ts, `RESERVATION_ACTION_PATH`). The form is
 * built to that route so that when the domain lands, no published page changes.
 *
 * What the form sends, which is the contract the route has to honour:
 *
 *   table    "roomId/tableId" of the picked table
 *   date     yyyy-mm-dd
 *   time     hh:mm
 *   guests   a whole number, 1..maxGuests
 *   name, phone, email, note
 *   consent  "yes"
 *   lang     the language the page was read in, for the reply
 *
 * The tab radios and the zoom box post along with these under the block's own
 * ids; the route reads the fields above and ignores the rest.
 */

/**
 * The form's words, in the languages this product ships. Not from the
 * catalogue: this renderer runs in Node during the prerender and produces every
 * language at once, while `t()` only ever knows the one the editor is in. The
 * same reasoning the template's own headings are written under.
 */
const RESERVE_COPY: Record<string, Record<string, string>> = {
  pl: {
    zoomIn: 'Powiększ',
    zoomOut: 'Pomniejsz',
    table: 'Stolik',
    pickHint: 'Wybierz stolik na planie.',
    booking: 'Rezerwacja',
    close: 'Zamknij',
    date: 'Data',
    time: 'Godzina',
    guests: 'Liczba osób',
    name: 'Imię i nazwisko',
    phone: 'Telefon',
    email: 'E-mail',
    note: 'Uwagi',
    optional: 'opcjonalnie',
    book: 'Zarezerwuj stolik',
    call: 'Wolisz zadzwonić?'
  },
  en: {
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    table: 'Table',
    pickHint: 'Pick a table on the plan.',
    booking: 'Booking',
    close: 'Close',
    date: 'Date',
    time: 'Time',
    guests: 'Guests',
    name: 'Name',
    phone: 'Phone',
    email: 'Email',
    note: 'Notes',
    optional: 'optional',
    book: 'Book a table',
    call: 'Prefer to call?'
  }
}

/*
  Never reached on a published page, which has no script to run it. In the
  editor the same markup is live React, and a submit there would post the
  owner's own preview to the reservations route, or without one reload the
  editor. The preview shows the form; it does not send it.
*/
const stopSubmit = (event: FormEvent) => event.preventDefault()

/*
  A table's value inside a double-quoted CSS string. Ids come from the planner
  and the schema lets them be any text, so quotes and backslashes are escaped
  the CSS way and `<` as well, since the rule is written into a <style> and a
  `</style>` inside an id would otherwise end it.
*/
const cssString = (value: string) =>
  value
    .replace(/[\\"]/g, '\\$&')
    .replace(/\n/g, '\\a ')
    .replace(/</g, '\\3c ')

function ReservationElement({
  element,
  ctx
}: {
  element: Of<'reservation'>
  ctx: RenderContext
}) {
  const copy = RESERVE_COPY[ctx.language] ?? RESERVE_COPY.en
  // A room nobody put a table in draws nothing, so it is not a room yet.
  const rooms = (element.rooms ?? []).filter(room => room.tables.length > 0)

  // No table to pick, no booking. A form for a venue with no plan is mode A,
  // which is phase 4's and the server's.
  if (rooms.length === 0) return null

  const label = pick(element.label, ctx) || copy.book
  const consent = pick(element.consent, ctx)
  const maxGuests = element.maxGuests ?? DEFAULT_MAX_GUESTS
  const fieldId = (name: string) => `${element.id}-${name}`
  const tables = rooms.flatMap(room =>
    room.tables.map(table => ({ room, table, value: `${room.id}/${table.id}` }))
  )
  /*
    The sheet's title names the table that was picked, and a stylesheet cannot
    read a radio's label. So the title holds one hidden name per table and a
    rule per table shows the one whose radio is checked: a line of CSS each,
    written into the page by the block that knows the tables. The rules live
    on this form's class, so two blocks on one page do not answer each other.
  */
  const chosenRules = tables
    .map(
      ({ value }) =>
        `.mp-reserve:has(.mp-room__pick-input[value="${cssString(value)}"]:checked) [data-mp-chosen="${cssString(value)}"]{display:inline}`
    )
    .join('')

  /*
    Every quarter hour the venue is open for, as a list the page draws itself.
    The browser's own clock field is a different control on every platform and
    none of them is this page's; this one is the venue's colours, its radius
    and its face, and it is a plain radio group underneath, so it arrives in
    the form like any other answer and moves under the arrow keys for free.
  */
  const slots = element.bookingHours
    ? quarterHours(element.bookingHours.from, element.bookingHours.to)
    : []

  /*
    The field shows the hour that is checked, and a stylesheet cannot read a
    radio's value: one rule per hour prints it, the same way the sheet's title
    names the picked table.
  */
  const timeRules = slots
    .map(
      slot =>
        `.mp-reserve:has(.mp-room__time-input[value="${cssString(slot)}"]:checked) .mp-room__time-value::after{content:"${cssString(slot)}"}`
    )
    .join('')

  return (
    /*
      Inline in the editor, a sheet on the page. The published form opens over
      the plan once a table is picked (a modal on a wide screen, a drawer from
      the bottom on a phone) and that is done in CSS off the picked radio. In
      the editor the same overlay would cover the whole app, and the owner
      wants to see the form in place anyway.
    */
    <form
      className={`mp-room mp-reserve mp-sheet-host${ctx.mode === 'edit' ? ' mp-sheet-host--inline' : ''}`}
      method="post"
      action={ctx.bookingAction}
      onSubmit={stopSubmit}
      // The preview must not nag the owner to fill in their own form.
      noValidate={ctx.mode === 'edit'}
    >
      <input type="hidden" name="lang" value={ctx.language} />
      {/*
        Tabs without a line of script, because a published page has none: a
        radio per room, the labels styled as the tab strip, and CSS showing the
        panel whose radio is checked. Keyboard arrows move between them for
        free, which a div-based tab strip would have had to implement.

        The radios come first and the panels last so `:checked ~` can reach
        across; `site.scss` pairs the nth radio with the nth panel.

        A venue with one room gets no strip at all. A single tab labelled with
        the only thing behind it is furniture, not a choice.
      */}
      <div
        className={`mp-room__tabs${rooms.length > 1 ? '' : ' mp-room__tabs--single'}`}
      >
        {rooms.map((room, index) => (
          <input
            key={`radio-${room.id}`}
            className="mp-room__radio"
            type="radio"
            name={`${element.id}-room`}
            id={`${element.id}-room-${room.id}`}
            defaultChecked={index === 0}
          />
        ))}
        <div className="mp-room__strip">
          {rooms.map(room => (
            <label
              key={`tab-${room.id}`}
              className="mp-room__tab"
              htmlFor={`${element.id}-room-${room.id}`}
            >
              {room.name}
            </label>
          ))}
          {/*
            When, beside the plan and not inside the sheet the plan opens: a
            guest picks a day and an hour and then a table, which is the order
            they think in and the order the venue will need once it can say
            which tables are still free at that hour.

            In a box of their own, and the box is load-bearing: the tab rules
            pair the nth radio with the nth label in the strip through
            `:nth-of-type`, and two more labels among the tabs' siblings would
            be counted with them. Nested, they are not siblings at all. The box
            also carries the `margin-left: auto` that pushes the pair and the
            zoom after it to the far end of the strip.
          */}
          <div className="mp-room__when-group">
          <label
            className="mp-room__when mp-room__when--date"
            htmlFor={fieldId('date')}
          >
            <span className="mp-room__when-label">{copy.date}</span>
            {/*
              Today, and nothing before it. `ctx.today` is the day the page is
              being rendered on: the editor's preview sets the day it is being
              looked at and the prerender the day it built the page, so a
              published page opens on its build date until it is built again
              or a server serves the form. That is the price of a page with no
              script in it.
            */}
            <input
              className="mp-room__when-input"
              id={fieldId('date')}
              type="date"
              name="date"
              min={ctx.today}
              defaultValue={ctx.today}
              required
            />
          </label>
          {/*
            The hour, as a list the page draws: the field opens it, a press
            anywhere else closes it again through the backdrop, and the hour
            that is checked is printed in the field by the rules above. Both
            fields carry a value from the start on purpose, since they stand
            outside the sheet the form is sent from and a required field a
            guest cannot see is a submit that fails with nothing on screen to
            explain it.

            A venue with no opening hours at all has no list to offer and
            falls back to the browser's own clock field.
          */}
          <div className="mp-room__when mp-room__when--time">
            <span className="mp-room__when-label" id={fieldId('time-label')}>
              {copy.time}
            </span>
            {slots.length > 0 ? (
              <div className="mp-room__timepick">
                <input
                  className="mp-room__time-open"
                  type="checkbox"
                  id={fieldId('time-open')}
                />
                <label
                  className="mp-room__when-input mp-room__time-field"
                  htmlFor={fieldId('time-open')}
                >
                  <span className="mp-visually-hidden">{copy.time}</span>
                  <span className="mp-room__time-value" aria-hidden="true" />
                </label>
                <label
                  className="mp-room__time-backdrop"
                  htmlFor={fieldId('time-open')}
                  aria-hidden="true"
                >
                  <span className="mp-visually-hidden">{copy.close}</span>
                </label>
                <div
                  className="mp-room__time-list"
                  role="group"
                  aria-labelledby={fieldId('time-label')}
                >
                  {slots.map(slot => (
                    <label
                      className="mp-room__time"
                      key={slot}
                      htmlFor={fieldId(`time-${slot.replace(':', '')}`)}
                    >
                      <input
                        className="mp-room__time-input"
                        id={fieldId(`time-${slot.replace(':', '')}`)}
                        type="radio"
                        name="time"
                        value={slot}
                        defaultChecked={slot === element.defaultTime}
                        required
                      />
                      <span>{slot}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <input
                className="mp-room__when-input"
                id={fieldId('time')}
                type="time"
                name="time"
                step={900}
                defaultValue={element.defaultTime}
                required={Boolean(element.defaultTime)}
              />
            )}
          </div>
          </div>
          {/*
            The zoom, and it lives INSIDE the strip rather than beside the
            room radios on purpose: the tab rules pair the nth radio with the
            nth panel through `:nth-of-type`, which counts every input among
            the siblings, so one more out there would shift the whole pairing
            by one. In here it is reached with `:has()` instead.
          */}
          <input
            className="mp-room__zoom"
            type="checkbox"
            id={`${element.id}-room-zoom`}
          />
          {/*
            Both labels are in the page and CSS shows the one for the next
            press, since a label with no script cannot change its own text.
          */}
          <label
            className="mp-room__zoom-tab mp-button mp-button--outline mp-button--small"
            htmlFor={`${element.id}-room-zoom`}
          >
            <span className="mp-room__zoom-in">{copy.zoomIn}</span>
            <span className="mp-room__zoom-out">{copy.zoomOut}</span>
          </label>
        </div>
        {/*
          The room's name is on its tab and nowhere else. Printed under the
          drawing as well it was the same word twice, a hand's width apart.
          The drawing still carries it as its accessible name, which is where
          it is needed and cannot be seen.
        */}
        {/*
          A `figure` and not a `div`, and the tag is load-bearing: the tab
          rules pair the nth radio with the nth panel through `:nth-of-type`,
          which counts by element type. As divs the panels were counted
          alongside the tab strip, which is also a div, and every room came
          out hidden.
        */}
        {rooms.map(room => (
          <figure
            className="mp-room__figure"
            key={room.id}
            style={roomPlanFrameStyle(room)}
          >
            <RoomPlanDrawing
              room={room}
              title={room.name}
              pick={{
                name: 'table',
                idPrefix: `${element.id}-table`,
                tableWord: copy.table
              }}
            />
          </figure>
        ))}
      </div>
      <p className="mp-reserve__hint">{copy.pickHint}</p>
      {/*
        How the sheet closes without a script: a radio in the table group with
        no table in it. Its labels are the close button and the backdrop, so
        either of them takes the pick away, and no pick is what hides the
        sheet. Picking again reopens it. The value is empty and the submit is
        inside the sheet, so this can never be what the form sends.
      */}
      <input
        className="mp-reserve__none"
        type="radio"
        name="table"
        value=""
        id={fieldId('table-none')}
      />
      <div
        className="mp-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={fieldId('title')}
      >
        {/*
          Hidden from assistive tech: the close button beside the title is the
          same control with the same name, and two of them would be noise.
        */}
        <label
          className="mp-sheet__backdrop"
          htmlFor={fieldId('table-none')}
          aria-hidden="true"
        >
          <span className="mp-visually-hidden">{copy.close}</span>
        </label>
        <div className="mp-sheet__panel">
          <div className="mp-sheet__head">
            <p className="mp-sheet__title" id={fieldId('title')}>
              {copy.booking}
              {tables.map(({ room, table, value }) => (
                <span
                  key={`chosen-${value}`}
                  className="mp-reserve__chosen"
                  data-mp-chosen={value}
                >
                  : {copy.table} {table.label}, {room.name}
                </span>
              ))}
            </p>
            <label className="mp-sheet__close" htmlFor={fieldId('table-none')}>
              <span aria-hidden="true">×</span>
              <span className="mp-visually-hidden">{copy.close}</span>
            </label>
          </div>
      <div className="mp-reserve__fields">
        <label className="mp-reserve__field" htmlFor={fieldId('guests')}>
          <span className="mp-reserve__label">{copy.guests}</span>
          <input
            className="mp-reserve__input"
            id={fieldId('guests')}
            type="number"
            name="guests"
            min={1}
            max={maxGuests}
            defaultValue={2}
            required
          />
        </label>
        <label className="mp-reserve__field" htmlFor={fieldId('name')}>
          <span className="mp-reserve__label">{copy.name}</span>
          <input
            className="mp-reserve__input"
            id={fieldId('name')}
            type="text"
            name="name"
            autoComplete="name"
            maxLength={120}
            required
          />
        </label>
        <label className="mp-reserve__field" htmlFor={fieldId('phone')}>
          <span className="mp-reserve__label">{copy.phone}</span>
          <input
            className="mp-reserve__input"
            id={fieldId('phone')}
            type="tel"
            name="phone"
            autoComplete="tel"
            maxLength={40}
            required
          />
        </label>
        <label className="mp-reserve__field" htmlFor={fieldId('email')}>
          <span className="mp-reserve__label">
            {copy.email} ({copy.optional})
          </span>
          <input
            className="mp-reserve__input"
            id={fieldId('email')}
            type="email"
            name="email"
            autoComplete="email"
            maxLength={160}
          />
        </label>
        <label
          className="mp-reserve__field mp-reserve__field--wide"
          htmlFor={fieldId('note')}
        >
          <span className="mp-reserve__label">
            {copy.note} ({copy.optional})
          </span>
          <textarea
            className="mp-reserve__input"
            id={fieldId('note')}
            name="note"
            maxLength={500}
            rows={3}
          />
        </label>
      </div>
      {consent && (
        <label className="mp-reserve__consent" htmlFor={fieldId('consent')}>
          <input
            className="mp-reserve__check"
            id={fieldId('consent')}
            type="checkbox"
            name="consent"
            value="yes"
            required
          />
          <span>{consent}</span>
        </label>
      )}
      <div className="mp-button-wrap">
        <button type="submit" className="mp-button mp-button--primary">
          {label}
        </button>
      </div>
      {element.phone && (
        <p className="mp-reserve__call">
          {copy.call}{' '}
          <a
            /*
              `tel:` wants the number without the spaces a venue types into the
              settings form; the text of the link keeps them, because that is
              the form somebody reads a number in.
            */
            href={`tel:${element.phone.replace(/[^\d+]/g, '')}`}
          >
            {element.phone}
          </a>
        </p>
      )}
        </div>
      </div>
      {/*
        Raw on purpose: React escapes the quotes in a text child of <style>
        (`&quot;`), and a <style> is raw text to the browser, so the selectors
        would arrive broken. Every value in here went through `cssString`.
      */}
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: chosenRules + timeRules }} />
    </form>
  )
}

function ElementRenderer({ element, ctx }: Props) {
  switch (element.type) {
    case 'heading': {
      const Tag = element.level === 2 ? 'h2' : 'h3'
      return (
        <Tag className={`mp-heading mp-heading--${element.level} ${alignClass(element.align)}`}>
          {pick(element.text, ctx)}
        </Tag>
      )
    }
    case 'text':
      return (
        <div className={`mp-text ${alignClass(element.align)}`}>
          <RichText doc={pickValue<RichTextDoc>(element.content, ctx)} />
        </div>
      )
    case 'image':
      return <ImageElement element={element} ctx={ctx} />
    case 'button': {
      const href = ctx.resolveHref(element)
      /*
        Only where the link leads somewhere. The editor's views resolve every
        link to '#', and a new tab of '#' is a second copy of the RMS.
      */
      const newTab =
        element.target.kind === 'url' && element.target.newTab && href !== '#'
      return (
        <div className={`mp-button-wrap ${alignClass(element.align)}`}>
          <a
            className={`mp-button mp-button--${element.variant}`}
            href={href}
            {...(newTab
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {pick(element.label, ctx)}
          </a>
        </div>
      )
    }
    case 'gallery':
      return <GalleryElement element={element} ctx={ctx} />
    case 'openingHours':
      return <OpeningHoursElement element={element} ctx={ctx} />
    case 'video':
      return <VideoElement element={element} />
    case 'map':
      return <MapElement element={element} ctx={ctx} />
    case 'reservation':
      return <ReservationElement element={element} ctx={ctx} />
    case 'menuFromPos':
      return <MenuElement element={element} ctx={ctx} />
    case 'divider':
      return <hr className="mp-divider" />
    case 'spacer':
      return <div className={`mp-spacer mp-spacer--${element.size}`} />
    default:
      return <MountPoint element={element} />
  }
}

export default ElementRenderer
